import React, { useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

const DeliveryOptionsCard = ({
    deliveryMethod = 'download',
    onDeliveryMethodChange,
    deliveryDetails = {},
    onDeliveryDetailsChange,
    invoiceSettings = {}
}) => {
    const { companyId } = useParams();
    const [touchedFields, setTouchedFields] = useState({});

    // Mark field as touched
    const handleFieldTouch = useCallback((fieldName) => {
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    }, []);

    // Handle delivery method change
    const handleMethodChange = useCallback((method) => {
        handleFieldTouch('deliveryMethod');
        onDeliveryMethodChange(method);
    }, [onDeliveryMethodChange, handleFieldTouch]);

    // Handle form field changes
    const handleFieldChange = useCallback((e) => {
        const { name, value } = e.target;
        handleFieldTouch(name);
        onDeliveryDetailsChange({ [name]: value });
    }, [onDeliveryDetailsChange, handleFieldTouch]);

    // Validation
    const validateEmail = useCallback((email) => {
        if (!email) return 'E-postadress krävs';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return 'Ogiltig e-postadress';
        return null;
    }, []);

    const validatePhone = useCallback((phone) => {
        if (!phone) return 'Telefonnummer krävs';
        const phoneRegex = /^(\+46|0)[0-9]{9,10}$/;
        if (!phoneRegex.test(phone.replace(/\s/g, ''))) return 'Ogiltigt telefonnummer';
        return null;
    }, []);

    const getFieldValidation = useCallback((fieldName) => {
        if (!touchedFields[fieldName]) return {};

        let error = null;
        if (fieldName === 'email' && deliveryMethod === 'email') {
            error = validateEmail(deliveryDetails.email);
        } else if (fieldName === 'phone' && deliveryMethod === 'sms') {
            error = validatePhone(deliveryDetails.phone);
        }

        return {
            isInvalid: !!error,
            error
        };
    }, [touchedFields, deliveryMethod, deliveryDetails, validateEmail, validatePhone]);

    // Check if settings are configured
    const settingsStatus = useMemo(() => ({
        email: !!invoiceSettings?.deliveryChannels?.emailFrom,
        sms: !!invoiceSettings?.deliveryChannels?.smsFrom
    }), [invoiceSettings]);

    return (
        <>
            <style>{`
        .delivery-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 0;
          margin-bottom: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .delivery-header {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border-bottom: 1px solid #e0f2fe;
          padding: 1.5rem;
        }
        
        .delivery-header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .delivery-icon {
          width: 48px;
          height: 48px;
          background: #0284c7;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }
        
        .delivery-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        
        .delivery-subtitle {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0.25rem 0 0 0;
        }
        
        .delivery-content {
          padding: 1.5rem;
        }
        
        .delivery-methods {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .delivery-method {
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
          position: relative;
        }
        
        .delivery-method:hover {
          border-color: #3b82f6;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        }
        
        .delivery-method.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .method-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          border-radius: 12px;
          color: #64748b;
          font-size: 1.5rem;
        }
        
        .delivery-method.selected .method-icon {
          background: #3b82f6;
          color: white;
        }
        
        .method-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .method-description {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 1rem;
        }
        
        .method-status {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .status-ready {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }
        
        .status-warning {
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fde68a;
        }
        
        .delivery-form {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
          margin-top: 1.5rem;
        }
        
        .form-section-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .form-group {
          margin-bottom: 1.5rem;
        }
        
        .form-label-modern {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        
        .required-asterisk {
          color: #dc2626;
          margin-left: 0.25rem;
        }
        
        .form-control-modern {
          display: block;
          width: 100%;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.5;
          color: #1f2937;
          background-color: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }
        
        .form-control-modern:focus {
          border-color: #3b82f6;
          outline: 0;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .form-control-modern.is-invalid {
          border-color: #dc2626;
        }
        
        .form-control-modern.is-invalid:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
        
        .invalid-feedback-modern {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: #dc2626;
          font-weight: 500;
        }
        
        .form-help {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: #6b7280;
        }
        
        .character-count {
          text-align: right;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }
        
        .settings-warning {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }
        
        .warning-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        
        .warning-title {
          font-weight: 700;
          color: #92400e;
          margin: 0;
        }
        
        .warning-text {
          font-size: 0.875rem;
          color: #92400e;
          margin: 0;
        }
        
        .warning-actions {
          margin-top: 0.75rem;
        }
        
        .btn-settings {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: white;
          color: #92400e;
          border: 1px solid #d97706;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
        }
        
        .btn-settings:hover {
          background: #f59e0b;
          color: white;
          text-decoration: none;
        }
        
        @media (max-width: 768px) {
          .delivery-methods {
            grid-template-columns: 1fr;
          }
          
          .delivery-header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }
      `}</style>

            <div className="delivery-card">
                {/* Header */}
                <div className="delivery-header">
                    <div className="delivery-header-content">
                        <div className="delivery-icon">
                            <i className="bi bi-truck"></i>
                        </div>
                        <div>
                            <h2 className="delivery-title">Leveransalternativ</h2>
                            <p className="delivery-subtitle">
                                Välj hur du vill leverera denna Offert till kunden
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="delivery-content">
                    {/* Delivery Methods */}
                    <div className="delivery-methods">
                        {/* Download */}
                        <div
                            className={`delivery-method ${deliveryMethod === 'download' ? 'selected' : ''}`}
                            onClick={() => handleMethodChange('download')}
                        >
                            <div className="method-icon">
                                <i className="bi bi-download"></i>
                            </div>
                            <h3 className="method-title">Ladda ner</h3>
                            <p className="method-description">
                                Spara som PDF på din enhet för manuell utskrift eller e-postutskick
                            </p>
                            <div className="method-status">
                                <span className="status-badge status-ready">
                                    <i className="bi bi-check-circle-fill"></i>
                                    Redo att använda
                                </span>
                            </div>
                        </div>

                        {/* Email */}
                        <div
                            className={`delivery-method ${deliveryMethod === 'email' ? 'selected' : ''}`}
                            onClick={() => handleMethodChange('email')}
                        >
                            <div className="method-icon">
                                <i className="bi bi-envelope"></i>
                            </div>
                            <h3 className="method-title">E-post</h3>
                            <p className="method-description">
                                Skicka offerten direkt till kundens e-postadress automatiskt
                            </p>
                            <div className="method-status">
                                {settingsStatus.email ? (
                                    <span className="status-badge status-ready">
                                        <i className="bi bi-check-circle-fill"></i>
                                        Redo att använda
                                    </span>
                                ) : (
                                    <span className="status-badge status-warning">
                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                        Konfiguration saknas
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* SMS */}
                        <div
                            className={`delivery-method ${deliveryMethod === 'sms' ? 'selected' : ''}`}
                            onClick={() => handleMethodChange('sms')}
                        >
                            <div className="method-icon">
                                <i className="bi bi-phone"></i>
                            </div>
                            <h3 className="method-title">SMS</h3>
                            <p className="method-description">
                                Skicka en länk till offerten via SMS till kundens telefon
                            </p>
                            <div className="method-status">
                                {settingsStatus.sms ? (
                                    <span className="status-badge status-ready">
                                        <i className="bi bi-check-circle-fill"></i>
                                        Redo att använda
                                    </span>
                                ) : (
                                    <span className="status-badge status-warning">
                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                        Konfiguration saknas
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Email Form */}
                    {deliveryMethod === 'email' && (
                        <div className="delivery-form">
                            <h3 className="form-section-title">
                                <i className="bi bi-envelope"></i>
                                E-postdetaljer
                            </h3>

                            <div className="form-group">
                                <label className="form-label-modern">
                                    Mottagarens e-postadress
                                    <span className="required-asterisk">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    className={`form-control-modern ${getFieldValidation('email').isInvalid ? 'is-invalid' : ''}`}
                                    value={deliveryDetails.email || ''}
                                    onChange={handleFieldChange}
                                    onBlur={() => handleFieldTouch('email')}
                                    placeholder="kund@exempel.se"
                                />
                                {getFieldValidation('email').isInvalid && (
                                    <div className="invalid-feedback-modern">
                                        {getFieldValidation('email').error}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label-modern">Ämne</label>
                                <input
                                    type="text"
                                    name="subject"
                                    className="form-control-modern"
                                    value={deliveryDetails.subject || ''}
                                    onChange={handleFieldChange}
                                    placeholder="Offert från [Ditt Företag]"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label-modern">
                                    Meddelande
                                    <span style={{ fontWeight: 400, color: '#6b7280' }}> (valfritt)</span>
                                </label>
                                <textarea
                                    name="message"
                                    className="form-control-modern"
                                    rows="3"
                                    value={deliveryDetails.message || ''}
                                    onChange={handleFieldChange}
                                    placeholder="Tack för ditt förtroende! Här kommer din offert."
                                    maxLength={500}
                                />
                                <div className="character-count">
                                    {deliveryDetails.message?.length || 0} / 500 tecken
                                </div>
                            </div>

                            {!settingsStatus.email && (
                                <div className="settings-warning">
                                    <div className="warning-header">
                                        <i className="bi bi-exclamation-triangle-fill" style={{ color: '#d97706' }}></i>
                                        <h4 className="warning-title">Avsändaradress saknas</h4>
                                    </div>
                                    <p className="warning-text">
                                        Du måste konfigurera en avsändaradress i inställningarna innan du kan skicka e-post.
                                    </p>
                                    <div className="warning-actions">
                                        <Link
                                            to={`/${companyId}/settings/invoice#leveransmetoder`}
                                            className="btn-settings"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="bi bi-gear"></i>
                                            Öppna inställningar
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* SMS Form */}
                    {deliveryMethod === 'sms' && (
                        <div className="delivery-form">
                            <h3 className="form-section-title">
                                <i className="bi bi-phone"></i>
                                SMS-detaljer
                            </h3>

                            <div className="form-group">
                                <label className="form-label-modern">
                                    Telefonnummer
                                    <span className="required-asterisk">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    className={`form-control-modern ${getFieldValidation('phone').isInvalid ? 'is-invalid' : ''}`}
                                    value={deliveryDetails.phone || ''}
                                    onChange={handleFieldChange}
                                    onBlur={() => handleFieldTouch('phone')}
                                    placeholder="+46 70 123 45 67"
                                />
                                {getFieldValidation('phone').isInvalid && (
                                    <div className="invalid-feedback-modern">
                                        {getFieldValidation('phone').error}
                                    </div>
                                )}
                                <div className="form-help">
                                    Ange telefonnummer med landskod (t.ex. +46 för Sverige)
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label-modern">
                                    Meddelande
                                    <span style={{ fontWeight: 400, color: '#6b7280' }}> (max 160 tecken)</span>
                                </label>
                                <textarea
                                    name="message"
                                    className="form-control-modern"
                                    rows="2"
                                    value={deliveryDetails.message || ''}
                                    onChange={handleFieldChange}
                                    placeholder="Din offert är klar. Klicka på länken för att visa."
                                    maxLength={160}
                                />
                                <div className="character-count">
                                    {deliveryDetails.message?.length || 0} / 160 tecken
                                </div>
                            </div>

                            {!settingsStatus.sms && (
                                <div className="settings-warning">
                                    <div className="warning-header">
                                        <i className="bi bi-exclamation-triangle-fill" style={{ color: '#d97706' }}></i>
                                        <h4 className="warning-title">SMS-avsändare saknas</h4>
                                    </div>
                                    <p className="warning-text">
                                        Du måste konfigurera en SMS-avsändare i inställningarna innan du kan skicka SMS.
                                    </p>
                                    <div className="warning-actions">
                                        <Link
                                            to={`/${companyId}/settings/invoice#leveransmetoder`}
                                            className="btn-settings"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="bi bi-gear"></i>
                                            Öppna inställningar
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DeliveryOptionsCard;